import { MODAL_IDS } from "@west-tennessee-consulting/ui";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";

import { logger } from "@/lib/logger";
import type { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";

import type { ClientRunHooks, ClientRunResolved } from "../types";

function toRecipients(
  contacts: Array<{ id: string; email: string; name?: string }>,
): EmailRecipient[] {
  return contacts.map((contact) => ({
    id: contact.id,
    type: "email" as const,
    email: contact.email,
    name: contact.name,
  }));
}

/** Open compose-email with resolved contact ids as recipients. */
export async function openComposeEmailModal(
  resolved: Pick<
    ClientRunResolved,
    "sourceEntity" | "sourceId" | "targetIds" | "prefetchedTargets"
  >,
  hooks: ClientRunHooks,
): Promise<void> {
  hooks.closeModal(MODAL_IDS.actions.resolver);

  let initialRecipients: EmailRecipient[];

  if (resolved.prefetchedTargets?.length) {
    initialRecipients = toRecipients(resolved.prefetchedTargets);
  } else {
    const supabase = getBrowserSupabaseClient();
    const { data: contacts, error } = await supabase
      .from("contacts")
      .select("id, name, email")
      .in("id", resolved.targetIds);

    if (error) {
      logger.error("Failed to fetch contacts for compose modal", error);
    }

    initialRecipients = toRecipients(
      (contacts ?? [])
        .filter(
          (
            contact,
          ): contact is { id: string; name: string | null; email: string } =>
            typeof contact.email === "string" && contact.email.trim().length > 0,
        )
        .map((contact) => ({
          id: contact.id,
          email: contact.email.trim(),
          name: contact.name ?? undefined,
        })),
    );
  }

  const primaryRecipient = initialRecipients[0];

  hooks.openModal(MODAL_IDS.messages.compose, {
    companyId:
      resolved.sourceEntity === "company" ? resolved.sourceId : undefined,
    mode: "compose",
    initialRecipients,
    customerEmail: primaryRecipient?.email,
    customerName: primaryRecipient?.name,
  });
}
